import { ArrowLeft, ArrowRight, Loader2, Lock } from "lucide-react";
import { formatChf } from "@/lib/data";
import { useLang } from "@/lib/i18n";

export default function BookingActions({ step, valid, onBack, onNext, onPay, paying, price, onboard = false, desktopCta = true }) {
  const { t } = useLang();
  const labels = [t("bk.cta_vehicles"), t("bk.cta_traveler"), t("bk.cta_payment")];
  const note = t("bk.cta_notes")[step];
  const isPay = step === 3;
  const enabled = Boolean(valid);
  const payLabel = onboard
    ? t("bk.final_board").replace("{p}", formatChf(price || 0))
    : t("bk.final_pay").replace("{p}", formatChf(price || 0));

  const nextButton = (testId) => (
    <button
      type="button"
      onClick={isPay ? onPay : onNext}
      disabled={!enabled || paying}
      data-testid={testId}
      className={`inline-flex h-[52px] min-w-44 items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold transition-[background-color,color,transform,box-shadow] ${
        enabled
          ? "bg-miles text-white shadow-[0_12px_30px_rgba(36,87,255,0.24)] hover:-translate-y-0.5 hover:bg-miles-dark"
          : "bg-ink/10 text-slate/55"
      }`}
    >
      {isPay ? <Lock className="h-4 w-4" strokeWidth={1.5} /> : null}
      {isPay ? (paying ? t("bk.paying") : payLabel) : labels[step]}
      {!isPay && <ArrowRight className="h-4 w-4" strokeWidth={1.5} />}
      {paying && <Loader2 className="h-4 w-4 animate-spin" />}
    </button>
  );

  return (
    <>
      <div className="mt-8 hidden items-center justify-between lg:flex" data-testid="booking-actions-desktop">
        {step > 0 ? (
          <button type="button" onClick={onBack} data-testid="booking-back-button"
            className="inline-flex items-center gap-2 rounded-full px-4 py-3 text-sm font-medium text-slate transition-colors hover:bg-white hover:text-ink">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {t("bk.back")}
          </button>
        ) : <span />}
        {desktopCta ? (
          <div className="text-right">
            {nextButton(isPay ? "booking-pay-button" : "booking-next-button")}
            <p className="mt-2 max-w-xs text-xs leading-relaxed text-slate">{note}</p>
          </div>
        ) : <span />}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-ink/10 bg-white/95 px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 backdrop-blur-xl lg:hidden" data-testid="booking-actions-mobile">
        <div className="flex items-center gap-3">
          {step > 0 ? (
            <button type="button" onClick={onBack} data-testid="booking-back-button-mobile"
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-ink/10 text-ink">
              <ArrowLeft className="h-4 w-4" />
            </button>
          ) : null}
          <div className="flex-1 [&>button]:h-12 [&>button]:w-full [&>button]:min-w-0">
            {nextButton(isPay ? "booking-pay-button-mobile" : "booking-next-button-mobile")}
            <p className="mt-2 text-center text-[11px] leading-snug text-slate">{note}</p>
          </div>
        </div>
      </div>
    </>
  );
}
